import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

/**
 * Predefined phrase library for the "Conclusioni" field of a scaffolding
 * verification. The dataset is grouped into four categories:
 *  1. Valutazione Stato — SINGLE selection (mutually exclusive)
 *  2. Geometria, Montaggio e Sicurezza Tecnica — MULTI selection
 *  3. Piani di Manutenzione e Interventi Futuri — MULTI selection
 *  4. Consigli e Scadenze — MULTI selection
 *
 * Each phrase has a stable `id` so we can render checkbox state without
 * relying on the (long) text. The `text` field is the exact wording that
 * gets inserted into the rich-text editor.
 */
export const CONCLUSIONI_LIBRARY = [
  {
    id: "stato",
    title: "1. Valutazione Stato",
    hint: "Selezione singola — scegli la frase che meglio descrive lo stato complessivo.",
    mode: "single",
    items: [
      {
        id: "stato-pessimo",
        label: "Pessimo stato",
        text:
          "Lo stato del magazzino è da considerare pessimo a causa delle numerose e gravi criticità riscontrate. Si richiede un intervento immediato di messa in sicurezza e il blocco delle campate più danneggiate fino al loro ripristino o sostituzione.",
      },
      {
        id: "stato-discreto",
        label: "Discreto",
        text:
          "Lo stato del magazzino è da considerare discreto. Gli scaffali presenti nel magazzino principale sono datati e posizionati su una pavimentazione non conforme con diversi avvallamenti. Valutare di procedere con il rifacimento del pavimento o lo spostamento del magazzino stesso.",
      },
      {
        id: "stato-migliorato",
        label: "Migliorato",
        text:
          "Lo stato delle scaffalature è in generale migliorato rispetto a quanto riscontrato nella verifica precedente, in quanto la maggior parte delle criticità segnalate nella precedente relazione sono state sanate.",
      },
      {
        id: "stato-buono",
        label: "Buono",
        text:
          "Lo stato del magazzino è da considerare buono, fatto salvo per le piccole criticità e i danni localizzati indicati nella presente relazione. Al momento della verifica non sono presenti danni strutturali gravi.",
      },
      {
        id: "stato-ottimo",
        label: "Ottimo",
        text:
          "Lo stato del magazzino è da considerare ottimo. Le strutture non presentano anomalie o deformazioni degne di nota, dimostrando una corretta gestione, manutenzione e utilizzo da parte degli operatori.",
      },
    ],
  },
  {
    id: "geometria",
    title: "2. Geometria, Montaggio e Sicurezza Tecnica",
    hint: "Selezione multipla.",
    mode: "multi",
    items: [
      {
        id: "geom-portata",
        label: "Modifica geometria / tabelle portata",
        text:
          "Per ragioni lavorative a volte viene modificata la geometria delle scaffalature o il numero di piani per campata; in questo caso si consiglia di effettuare una valutazione tecnica della portata e, nel caso, di inserire le relative tabelle corrette in modo che gli operatori possano avere le giuste informazioni.",
      },
      {
        id: "geom-montaggio",
        label: "Procedure di montaggio / spostamento",
        text:
          "Prestare attenzione in fase di montaggio o spostamento delle scaffalature sulle corrette procedure da attuare, e compilare sempre un rapporto di avvenuto montaggio e verifica.",
      },
      {
        id: "geom-utilizzo",
        label: "Monitoraggio stato scaffali",
        text:
          "Tenere sempre monitorato lo stato degli scaffali in uso e valutare il corretto utilizzo in base alle caratteristiche originali e allo stato attuale.",
      },
    ],
  },
  {
    id: "manutenzione",
    title: "3. Piani di Manutenzione e Interventi Futuri",
    hint: "Selezione multipla.",
    mode: "multi",
    items: [
      {
        id: "manut-piano",
        label: "Piano di manutenzione generale",
        text:
          "Continuare con un piano di manutenzione generale che vada a sanare le situazioni anomale indicate, partendo dalle più gravi (danni ai montanti o pericoli per caduta materiale).",
      },
      {
        id: "manut-layout",
        label: "Riformulazione layout",
        text:
          "Un'attenta riformulazione del layout delle scaffalature andrebbe a ottimizzare gli spazi aumentando la sicurezza. Salvare il salvabile e procedere con la sostituzione dei componenti necessari con prodotti nuovi.",
      },
      {
        id: "manut-sostituzione",
        label: "Sostituzione scaffalature datate",
        text:
          "Continuare nel piano di manutenzione generale intrapreso con sostituzione delle scaffalature più datate e avendo cura delle altre.",
      },
    ],
  },
  {
    id: "consigli",
    title: "4. Consigli e Scadenze",
    hint: "Selezione multipla.",
    mode: "multi",
    items: [
      {
        id: "cons-formazione",
        label: "Formazione operatori",
        text:
          "Si consiglia una formazione sul corretto utilizzo delle scaffalature per gli addetti al magazzino, e su come distribuire correttamente i carichi.",
      },
      {
        id: "cons-segnalazione",
        label: "Procedura segnalazione danni",
        text:
          "Si consiglia l'attuazione di una procedura di segnalazione danni da parte degli operatori.",
      },
      {
        id: "cons-uni",
        label: "Verifica annuale UNI-EN 15635",
        text:
          "Si consiglia, come previsto dalla norma UNI-EN 15635, una verifica visiva ad un intervallo non maggiore di 12 mesi.",
      },
    ],
  },
];

/**
 * Escape user-facing characters that have meaning inside HTML so the inserted
 * phrase renders verbatim and isn't reinterpreted as markup. The phrase texts
 * above are static + sanitised at source, but we still escape to be safe.
 */
function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Build the HTML that gets inserted into the rich-text editor.
 * Each selected phrase becomes its own `<p>` so the user can edit independently.
 * Phrases are emitted in the deterministic order of the library (state →
 * geometria → manutenzione → consigli) regardless of click order.
 */
function buildInsertionHTML(selected) {
  const parts = [];
  for (const cat of CONCLUSIONI_LIBRARY) {
    for (const it of cat.items) {
      if (selected[it.id]) parts.push(`<p>${escapeHtml(it.text)}</p>`);
    }
  }
  return parts.join("");
}

export default function ConclusioniPhrasePicker({ open, onClose, onInsert }) {
  const [selected, setSelected] = useState({});

  // Reset selection every time the dialog re-opens so previous picks don't
  // accidentally get re-inserted.
  useEffect(() => {
    if (open) setSelected({});
  }, [open]);

  if (!open) return null;

  const toggleSingle = (catId, itemId) => {
    setSelected((s) => {
      const next = { ...s };
      // Clear all items in this category, then enable the picked one.
      const cat = CONCLUSIONI_LIBRARY.find((c) => c.id === catId);
      cat.items.forEach((i) => { delete next[i.id]; });
      if (s[itemId]) {
        // tapping the same one again deselects it
      } else {
        next[itemId] = true;
      }
      return next;
    });
  };

  const toggleMulti = (itemId) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[itemId]) delete next[itemId];
      else next[itemId] = true;
      return next;
    });
  };

  const count = Object.values(selected).filter(Boolean).length;

  const handleInsert = () => {
    const html = buildInsertionHTML(selected);
    if (!html) {
      onClose();
      return;
    }
    onInsert(html);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      data-testid="conclusioni-phrase-picker"
      onClick={onClose}
    >
      <div className="bru-card w-full max-w-3xl p-6 mt-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-2xl font-bold">Inserisci frasi predefinite</h3>
            <p className="text-xs text-neutral-500 mt-1">
              Seleziona le frasi che vuoi inserire nel campo &quot;Conclusioni&quot;.
              Verranno aggiunte alla posizione del cursore e potrai modificarle
              liberamente.
            </p>
          </div>
          <button
            onClick={onClose}
            className="bru-btn bru-btn-ghost !px-2 !py-1"
            data-testid="close-phrase-picker"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          {CONCLUSIONI_LIBRARY.map((cat) => (
            <div key={cat.id} data-testid={`phrase-category-${cat.id}`}>
              <div className="flex items-baseline justify-between mb-2">
                <h4 className="font-bold text-base">{cat.title}</h4>
                <span className="text-[11px] uppercase tracking-wider text-neutral-500">
                  {cat.mode === "single" ? "Selezione singola" : "Selezione multipla"}
                </span>
              </div>
              <div className="space-y-2">
                {cat.items.map((it) => {
                  const checked = !!selected[it.id];
                  const inputType = cat.mode === "single" ? "radio" : "checkbox";
                  return (
                    <label
                      key={it.id}
                      className={`flex gap-3 p-3 border-[1.5px] cursor-pointer transition ${
                        checked
                          ? "border-black bg-[#FFF6EE]"
                          : "border-neutral-200 hover:border-neutral-400 bg-white"
                      }`}
                      data-testid={`phrase-item-${it.id}`}
                    >
                      <input
                        type={inputType}
                        name={cat.mode === "single" ? `cat-${cat.id}` : undefined}
                        checked={checked}
                        onChange={() =>
                          cat.mode === "single"
                            ? toggleSingle(cat.id, it.id)
                            : toggleMulti(it.id)
                        }
                        className="mt-1 shrink-0"
                        data-testid={`phrase-checkbox-${it.id}`}
                      />
                      <div className="text-sm">
                        <div className="font-bold mb-1">{it.label}</div>
                        <div className="text-neutral-700 leading-snug">{it.text}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 mt-4 border-t border-neutral-200">
          <span className="text-sm text-neutral-600" data-testid="phrase-selected-count">
            {count === 0 ? "Nessuna frase selezionata" : `${count} ${count === 1 ? "frase selezionata" : "frasi selezionate"}`}
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="bru-btn bru-btn-secondary"
              data-testid="phrase-picker-cancel"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleInsert}
              disabled={count === 0}
              className="bru-btn bru-btn-primary disabled:opacity-60"
              data-testid="phrase-picker-insert"
            >
              INSERISCI
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
